var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
const ERROR_IMG_SRC = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4KCg==';
// Hook per rilevare la dimensione dello schermo
function useScreenSize() {
    const [screenSize, setScreenSize] = useState('desktop');
    useEffect(() => {
        function updateScreenSize() {
            const width = window.innerWidth;
            if (width < 768) {
                setScreenSize('mobile');
            }
            else if (width < 1024) {
                setScreenSize('tablet');
            }
            else {
                setScreenSize('desktop');
            }
        }
        updateScreenSize();
        window.addEventListener('resize', updateScreenSize);
        return () => window.removeEventListener('resize', updateScreenSize);
    }, []);
    return screenSize;
}
// Hook per rilevare il supporto WebP
function useWebPSupport() {
    const [supportsWebP, setSupportsWebP] = useState(null);
    useEffect(() => {
        const webP = new Image();
        webP.onload = webP.onerror = () => {
            setSupportsWebP(webP.height === 2);
        };
        webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    }, []);
    return supportsWebP;
}
// Funzione per generare i path delle immagini automaticamente
function generateImageSources(basePath) {
    return {
        desktop: {
            webp: `${basePath}_desktop.webp`,
            jpg: `${basePath}_desktop.jpg`
        },
        tablet: {
            webp: `${basePath}_tablet.webp`,
            jpg: `${basePath}_tablet.jpg`
        },
        mobile: {
            webp: `${basePath}_mobile.webp`,
            jpg: `${basePath}_mobile.jpg`
        }
    };
}
export function ImageWithFallback(_a) {
    var { src, sources, baseName, credit, showCredit = true, alt, style, className } = _a, rest = __rest(_a, ["src", "sources", "baseName", "credit", "showCredit", "alt", "style", "className"]);
    const [didError, setDidError] = useState(false);
    const [currentSrc, setCurrentSrc] = useState('');
    const screenSize = useScreenSize();
    const supportsWebP = useWebPSupport();
    // Determina il src corretto da usare
    useEffect(() => {
        // Se è fornito un src tradizionale, usalo
        if (src && !sources && !baseName) {
            setCurrentSrc(src);
            return;
        }
        // Genera sources automaticamente se fornito baseName
        const imageSources = baseName ? generateImageSources(baseName) : sources;
        if (!imageSources) {
            setCurrentSrc(src || '');
            return;
        }
        // Seleziona la risoluzione appropriata
        const deviceSources = imageSources[screenSize] || imageSources.desktop;
        if (!deviceSources) {
            setCurrentSrc(src || '');
            return;
        }
        // Seleziona il formato (WebP se supportato, altrimenti JPEG)
        let selectedSrc = '';
        if (supportsWebP === true && deviceSources.webp) {
            selectedSrc = deviceSources.webp;
        }
        else if (supportsWebP === false && deviceSources.jpg) {
            selectedSrc = deviceSources.jpg;
        }
        else if (supportsWebP === null) {
            // WebP support ancora in fase di rilevamento, usa JPEG come fallback sicuro
            selectedSrc = deviceSources.jpg || deviceSources.webp || '';
        }
        setCurrentSrc(selectedSrc || src || '');
    }, [src, sources, baseName, screenSize, supportsWebP]);
    const handleError = () => {
        setDidError(true);
    };
    // Reset error state quando cambia l'immagine
    useEffect(() => {
        setDidError(false);
    }, [currentSrc]);
    // Determina se mostrare il credit
    const shouldShowCredit = credit && showCredit && credit.display;
    // Stili per il credit
    const creditPosition = (credit === null || credit === void 0 ? void 0 : credit.position) || 'bottom-right';
    const defaultCreditStyles = Object.assign(Object.assign({}, getPositionStyles(creditPosition)), credit === null || credit === void 0 ? void 0 : credit.style);
    const renderImage = () => {
        return didError ? (_jsx("div", { className: `inline-block bg-gray-100 text-center align-middle ${className !== null && className !== void 0 ? className : ''}`, style: style, children: _jsx("div", { className: "flex items-center justify-center w-full h-full", children: _jsx("img", Object.assign({ src: ERROR_IMG_SRC, alt: "Error loading image" }, rest, { "data-original-url": currentSrc, "data-screen-size": screenSize, "data-webp-support": supportsWebP })) }) })) : (_jsx("img", Object.assign({ src: currentSrc, alt: alt, className: className, style: style }, rest, { onError: handleError, "data-screen-size": screenSize, "data-webp-support": supportsWebP })));
    };
    // Se non c'è credit, ritorna solo l'immagine
    if (!shouldShowCredit) {
        return renderImage();
    }
    // Con credit, wrappa in un container relativo
    return (_jsxs(_Fragment, { children: [renderImage(), _jsx("div", { className: credit === null || credit === void 0 ? void 0 : credit.className, style: defaultCreditStyles, children: (credit === null || credit === void 0 ? void 0 : credit.link) ? (_jsx("a", { href: credit.link, target: "_blank", rel: "noopener noreferrer", className: "text-inherit hover:text-white transition-colors duration-200", style: { textDecoration: 'none' }, children: credit === null || credit === void 0 ? void 0 : credit.display })) : (_jsx("span", { children: credit === null || credit === void 0 ? void 0 : credit.display })) })] }));
}
// Helper function per posizionare il credit
function getPositionStyles(position) {
    switch (position) {
        case 'bottom-left':
            return { bottom: '8px', left: '8px' };
        case 'bottom-right':
            return { bottom: '8px', right: '8px' };
        case 'top-left':
            return { top: '8px', left: '8px' };
        case 'top-right':
            return { top: '8px', right: '8px' };
        default:
            return { bottom: '8px', right: '8px' };
    }
}
